import { ArrowLeft, Activity, Moon, Heart, Dumbbell } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Logo1ClinHorizontal } from '../components/Logo1Clin';

export default function GoogleFitDisclosure() {
  const navigate = useNavigate();

  const scopes = [
    {
      scope: 'fitness.activity.read',
      name: 'Activity Data',
      icon: Dumbbell,
      color: 'emerald',
      data: 'Daily step count, calories burned, active minutes, physical activity segments',
      justification: '1Clin is a healthcare practice management platform that enables doctors to monitor their patients\' physical activity remotely. Patients connect Google Fit to automatically import their daily activity data (steps, calories burned) into the 1Clin Health Tracking dashboard. This data is displayed as trends and charts, allowing doctors to set personalized activity targets (e.g., daily steps goal) and receive compliance alerts when patients fall below prescribed thresholds for 3+ consecutive days. Without this scope, patients would need to manually enter their activity data daily, which reduces accuracy and compliance. The automated sync ensures doctors have reliable, real-time data to make informed clinical decisions about their patients\' physical rehabilitation, weight management, and cardiovascular health programs.',
    },
    {
      scope: 'fitness.body.read',
      name: 'Body Metrics',
      icon: Activity,
      color: 'blue',
      data: 'Weight, height, body fat percentage (if recorded by user)',
      justification: '1Clin doctors prescribe health targets including weight goals for patients managing conditions like obesity, diabetes, and hypertension. The body.read scope allows automatic import of weight measurements from connected scales and Google Fit, eliminating manual data entry errors. This weight data is plotted alongside other health metrics (activity, nutrition, sleep) to give doctors a complete picture of patient progress. The data is used exclusively within the patient\'s health record on 1Clin and shared only with their registered healthcare provider. Weight tracking is essential for monitoring treatment effectiveness in metabolic and cardiovascular conditions.',
    },
    {
      scope: 'fitness.sleep.read',
      name: 'Sleep Data',
      icon: Moon,
      color: 'indigo',
      data: 'Sleep duration, bed time, wake time, sleep sessions',
      justification: 'Sleep quality directly impacts patient health outcomes across multiple conditions including mental health, cardiovascular disease, diabetes management, and post-operative recovery. 1Clin imports sleep data from Google Fit to display sleep duration trends in the patient\'s Health Tracking dashboard. Doctors can set minimum sleep hour targets for patients and receive automated compliance alerts when patients consistently sleep below prescribed thresholds. This data replaces unreliable self-reported sleep logs with objective measurements from the patient\'s phone or wearable. Sleep trend analysis helps doctors identify patterns correlated with other health indicators like stress levels and medication adherence.',
    },
    {
      scope: 'fitness.heart_rate.read',
      name: 'Heart Rate Data',
      icon: Heart,
      color: 'rose',
      data: 'Average heart rate, resting heart rate (from wearables)',
      justification: '1Clin already captures heart rate through its proprietary Face Vitals Scanner (camera-based rPPG technology). Heart rate data from Google Fit supplements these periodic scans with continuous measurements from smartwatches and fitness bands. This is critical for patients with cardiovascular conditions, hypertension, or those on beta-blockers where continuous heart rate monitoring can detect dangerous anomalies. The imported heart rate data is displayed alongside face vitals data in the patient\'s health record, giving doctors a more complete cardiovascular profile. This dual-source approach (face scan + wearable) improves diagnostic accuracy and enables earlier detection of cardiac irregularities.',
    },
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-slate-300">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <Logo1ClinHorizontal width={140} />
          <button onClick={() => navigate('/')} className="flex items-center gap-1.5 text-sm text-teal-400 hover:text-teal-300 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        </div>

        <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">Google Fit Data Disclosure</h1>
        <p className="text-sm text-slate-500 mb-2">Last Updated: August 24, 2026</p>
        <p className="text-sm text-slate-400 mb-8">This page describes how 1Clin uses data from the Google Fit API and justifies each requested permission scope.</p>

        <div className="bg-teal-950/30 border border-teal-800/50 rounded-xl p-5 mb-8">
          <h2 className="text-lg font-semibold text-white mb-3">Application Overview</h2>
          <p className="text-sm leading-relaxed">
            <strong className="text-white">1Clin</strong> is a healthcare practice management platform built by <strong className="text-white">Oneprick Labs Private Limited</strong> (India). 
            It connects doctors, patients, and healthcare vendors. The Google Fit integration is part of the <strong className="text-white">Patient Health Tracking</strong> module, 
            which enables patients to sync fitness data automatically and allows their registered doctors to monitor health metrics remotely, set personalized targets, 
            and receive compliance alerts — all within a secure, DPDP Act 2023 compliant environment.
          </p>
        </div>

        <h2 className="text-xl font-bold text-white mb-4">Scope-by-Scope Justification</h2>

        <div className="space-y-6">
          {scopes.map((s) => (
            <div key={s.scope} className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-10 h-10 rounded-lg bg-${s.color}-500/20 flex items-center justify-center`}>
                  <s.icon className={`w-5 h-5 text-${s.color}-400`} />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-white">{s.name}</h3>
                  <code className="text-xs text-teal-400">https://www.googleapis.com/auth/{s.scope}</code>
                </div>
              </div>

              <div className="mb-3">
                <p className="text-xs font-semibold text-slate-400 uppercase mb-1">Data Accessed</p>
                <p className="text-sm text-slate-300">{s.data}</p>
              </div>

              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase mb-1">Justification</p>
                <p className="text-sm text-slate-300 leading-relaxed">{s.justification}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 bg-slate-900/50 border border-slate-800/50 rounded-xl p-5">
          <h2 className="text-lg font-semibold text-white mb-3">Data Handling Practices</h2>
          <ul className="list-disc pl-5 space-y-2 text-sm">
            <li><strong className="text-white">Limited Use:</strong> 1Clin's use of Google Fit data complies with the <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer" className="text-teal-400 underline">Google API Services User Data Policy</a>, including the Limited Use requirements. Data is used exclusively for the health tracking features described above.</li>
            <li><strong className="text-white">No Advertising:</strong> Google Fit data is never used for advertising, marketing, or sold to third parties.</li>
            <li><strong className="text-white">Read-Only Access:</strong> We only request read access. 1Clin never writes or modifies data in the user's Google Fit account.</li>
            <li><strong className="text-white">User Control:</strong> Patients can disconnect Google Fit at any time from their Health dashboard, immediately stopping all data syncing.</li>
            <li><strong className="text-white">Data Storage:</strong> Synced data is stored on encrypted servers within India in compliance with Indian data localization requirements.</li>
            <li><strong className="text-white">Access Restricted:</strong> Google Fit data is visible only to the patient and their registered healthcare provider(s) on the 1Clin platform.</li>
          </ul>
        </div>

        <div className="mt-8 bg-slate-900/50 border border-slate-800/50 rounded-xl p-5">
          <h2 className="text-lg font-semibold text-white mb-3">Contact</h2>
          <p className="text-sm text-slate-300">For questions about our Google Fit integration or data practices:</p>
          <ul className="mt-2 space-y-1 text-sm">
            <li><strong className="text-slate-200">Company:</strong> Oneprick Labs Private Limited</li>
            <li><strong className="text-slate-200">Email:</strong> onepricklabs@gmail.com</li>
            <li><strong className="text-slate-200">Website:</strong> <a href="https://1clin.com" className="text-teal-400 underline">https://1clin.com</a></li>
          </ul>
        </div>

        <div className="mt-10 pt-6 border-t border-slate-800 text-center text-xs text-slate-600">
          &copy; {new Date().getFullYear()} Oneprick Labs Private Limited. All rights reserved.
        </div>
      </div>
    </div>
  );
}
